import { Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { BrandLogo } from '@/components/ui/brand-logo'
import { cn } from '@/lib/utils'
import { AccountControls } from '@/components/organisms/account-controls'
import { routeHasTrail, trailParent } from '@/lib/shell-chrome'
import { usePageTrailValue, type TrailItem } from '@/hooks/usePageTrail'

/**
 * The in-app top bar — round 18, frames `18a`, `18b`, `18d`, `18e`.
 *
 * It used to carry `SECTION_LABELS`, an eleven-entry longest-prefix-wins table
 * mapping route prefixes to the same `nav.*` keys the sidebar uses. On `/events`
 * that printed «Arrangementer» three times over (sidebar item + bar + `h1`); on
 * `/event/:id/offer/edit` it printed «Arrangementer» above the offer builder,
 * because a prefix table cannot express nesting. It is gone. What decides the
 * bar now lives in `lib/shell-chrome.ts`, derived from the router.
 *
 * Two rendering rules, and they are independent:
 *
 *  - **Below `lg`** the bar always renders. It is the mobile header: it carries
 *    the brand, and it is the only home of the system controls, because
 *    `sidebar.tsx` is `hidden lg:flex`. `18d` decides this — «Mobil beholder
 *    baren uendret» — and it is what stops the phase from silently deleting
 *    sign-out from every phone.
 *  - **At `lg+`** the bar renders only where the route has depth the sidebar
 *    and the `h1` cannot state: five nested routes. The other fifteen get their
 *    56px back, and the system controls live in the sidebar footer.
 *
 * The bar's own cluster is therefore `lg:hidden` — one instance of each
 * control at every viewport, never two.
 */
export function Topbar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const published = usePageTrailValue()
  const hasTrail = routeHasTrail(pathname)


  // Until the page publishes (chunk still loading, record not resolved) the bar
  // shows the route's parent, so it is never an empty 56px strip.
  const parent = trailParent(pathname)
  const trail: TrailItem[] =
    published.length > 0
      ? published
      : parent
        ? [{ label: t(parent.labelKey), to: parent.to }]
        : []

  return (
    <header
      className={cn(
        'shrink-0 items-center justify-between gap-4 border-b border-[var(--border-default)]',
        'bg-[var(--surface-default)] px-4 sm:px-6',
        // Height is now a plain 56px: the iOS safe-area inset moved to the app
        // shell in AppLayout, so it no longer disappears with the bar.
        'h-[var(--app-topbar-height)]',
        hasTrail ? 'flex' : 'flex lg:hidden'
      )}
    >
      {/* Left: brand (mobile) / depth trail (desktop) */}
      <div className="flex min-w-0 items-center gap-3">
        <BrandLogo size="md" to="/dashboard" className="lg:hidden" animate wordmarkFrom="sm" />
        {hasTrail && trail.length > 0 && <PageTrail items={trail} className="hidden lg:flex" />}
      </div>

      {/* Right: the system controls — mobile only. At lg+ they live in the
          sidebar footer, so a retained bar never duplicates them. */}
      <AccountControls variant="bar" className="lg:hidden" />
    </header>
  )
}

/**
 * The depth trail — `18b`: «Baren beholdes, men får en jobb h1 ikke kan gjøre.
 * Dybde og retur, ikke ekko.»
 *
 * Named, because the shell already has two other `<nav>` landmarks and axe's
 * `landmark-unique` needs them distinguishable.
 */
function PageTrail({ items, className }: { items: TrailItem[]; className?: string }) {
  const { t } = useTranslation()
  return (
    <nav aria-label={t('nav.breadcrumb')} className={cn('min-w-0 items-center', className)}>
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {items.map((item, i) => {
          const last = i === items.length - 1
          return (
            <Fragment key={`${item.to ?? 'current'}-${i}`}>
              {i > 0 && (
                <li aria-hidden="true" className="shrink-0 text-[var(--text-muted)]">
                  <ChevronRight className="size-3.5" />
                </li>
              )}
              <li className="min-w-0">
                {item.to && !last ? (
                  <Link
                    to={item.to}
                    className="block truncate rounded-[var(--radius-sm)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] focus-ring"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    aria-current={last ? 'page' : undefined}
                    className="block truncate font-semibold text-[var(--text-primary)]"
                  >
                    {item.label}
                  </span>
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
